import { useEffect } from "react";

import { useFormikContext } from "formik";

import { useBillableOperationsQuery } from "@sso/apis/billableOperations";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectSoftwareLicenceId } from "@sso/store/slices/softwareLicence";
import { Currency, LicenceType, SoftwareLicenceFormValues } from "@sso/types";
import { Typography } from "@sso/ui/Typography";

import AddCurrencyRules from "./AddCurrencyRules";

interface AddCurrencyApiRulesFormProps {
  currency?: Currency;
}

const AddCurrencyApiRulesForm = ({ currency }: AddCurrencyApiRulesFormProps) => {
  const softwareId = useAppSelector(selectSoftwareLicenceId);
  const { data } = useBillableOperationsQuery(softwareId);
  const { setFieldValue, values, errors } = useFormikContext<SoftwareLicenceFormValues>();

  useEffect(() => {
    if (!data) {
      return;
    }

    if (!values.pricingRules || !values.pricingRules.length) {
      const defaultRules = data.map(billableOperation => {
        return {
          billableOperationId: billableOperation.billableOperationId,
          pricingRules: [
            {
              minRange: 1,
              billableOperationId: billableOperation.billableOperationId,
              isLast: false,
            },
          ],
        };
      });
      setFieldValue("apiPricingRules", defaultRules);
    } else {
      const defaultRules = data.map(billableOperation => {
        const pricingRules = values.pricingRules
          ?.filter(x => x.billableOperationId === billableOperation.billableOperationId)
          .sort((a, b) => a.minRange! - b.minRange!);

        return {
          billableOperationId: billableOperation.billableOperationId,
          pricingRules: pricingRules ?? [],
        };
      });

      setFieldValue("apiPricingRules", defaultRules);
    }
  }, [data, values.pricingRules, setFieldValue]);

  return (
    <>
      {values.apiPricingRules?.length !== 0 &&
        data &&
        values.apiPricingRules?.map((apiRule, index) => {
          return (
            <div key={`${apiRule.billableOperationId}-${index}`}>
              <Typography margin="0.5rem 0">
                {data.find(x => x.billableOperationId === apiRule.billableOperationId)?.description}
              </Typography>
              <AddCurrencyRules
                fieldAccessor={`apiPricingRules[${index}].pricingRules`}
                currency={currency}
                rules={apiRule.pricingRules ?? []}
                licenceType={LicenceType.API}
              />
            </div>
          );
        })}
    </>
  );
};

export default AddCurrencyApiRulesForm;
